import apiRequest from "../../utils/apiRequest";
import {
  AbsenceRequest,
  AvailabilityImpactRequest,
  WeeklyAvailabilityRequest,
} from "./types";

/**
 * Service disponibilité des médecins. Lecture (règles, créneaux libres) pour
 * tout le personnel ; mutations réservées à l'administrateur et au médecin
 * sur sa propre disponibilité (contrôlé côté serveur).
 *
 * Les mutations prennent `confirm` : sans lui, une modification qui touche des
 * RDV futurs est refusée en 409 `error.availability.has_appointments`. Le
 * client demande l'aperçu d'impact avant et ne passe `confirm=true` qu'après
 * confirmation explicite.
 */

const confirmQuery = (confirm: boolean) => (confirm ? "?confirm=true" : "");

/** Semaine type + absences : `DoctorAvailability`. */
export const getDoctorAvailability = (doctorId: number) =>
  apiRequest.get(`/availability/doctors/${doctorId}`);

/** Créneaux libres de `from` à `to` inclus (≤ 31 jours) : `Slot[]`. */
export const getDoctorSlots = (doctorId: number, from: string, to: string) => {
  const qs = new URLSearchParams({ from, to }).toString();
  return apiRequest.get(`/availability/doctors/${doctorId}/slots?${qs}`);
};

/** RDV futurs qui sortiraient de la disponibilité : `ImpactedAppointment[]`. */
export const previewAvailabilityImpact = (doctorId: number, request: AvailabilityImpactRequest) =>
  apiRequest.post(`/availability/doctors/${doctorId}/impact`, request);

/** Ajoute une plage à la semaine type. Renvoie l'id (`Long`). */
export const createWeeklyAvailability = (
  doctorId: number,
  request: WeeklyAvailabilityRequest,
  confirm: boolean
) => apiRequest.post(`/availability/doctors/${doctorId}/weekly${confirmQuery(confirm)}`, request);

export const updateWeeklyAvailability = (
  weeklyId: number,
  request: WeeklyAvailabilityRequest,
  confirm: boolean
) => apiRequest.put(`/availability/weekly/${weeklyId}${confirmQuery(confirm)}`, request);

export const deleteWeeklyAvailability = (weeklyId: number, confirm: boolean) =>
  apiRequest.del(`/availability/weekly/${weeklyId}${confirmQuery(confirm)}`);

/** Ajoute une absence (journées entières). Renvoie l'id (`Long`). */
export const createAbsence = (doctorId: number, request: AbsenceRequest, confirm: boolean) =>
  apiRequest.post(`/availability/doctors/${doctorId}/absences${confirmQuery(confirm)}`, request);

export const updateAbsence = (absenceId: number, request: AbsenceRequest, confirm: boolean) =>
  apiRequest.put(`/availability/absences/${absenceId}${confirmQuery(confirm)}`, request);

/** Supprimer une absence rend de la disponibilité : aucun RDV touché, pas de `confirm`. */
export const deleteAbsence = (absenceId: number) =>
  apiRequest.del(`/availability/absences/${absenceId}`);
